/**
 * Theme infrastructure: the single place that touches `localStorage`, `matchMedia` and the `data-theme`
 * attribute. The store holds the preference; this module turns it into something the CSS can see.
 *
 * Only ever stores the literal string `light`, `dark` or `system` — a display preference, never PHI or
 * PII, so it stays clear of US-003 AC-004.
 */

export const THEME_STORAGE_KEY = 'portal-theme';

/** What the user chose. `system` defers to the OS and keeps following it as it changes. */
export type ThemePreference = 'light' | 'dark' | 'system';

/** What actually gets painted. `system` is always resolved to one of these before reaching the DOM. */
export type ResolvedTheme = 'light' | 'dark';

const PREFERENCES: readonly ThemePreference[] = ['light', 'dark', 'system'];

function isThemePreference(value: unknown): value is ThemePreference {
  return typeof value === 'string' && PREFERENCES.includes(value as ThemePreference);
}

/**
 * Reads the stored preference, defaulting to `system` on a first visit.
 *
 * Private-mode Safari throws on `localStorage` access rather than returning null, and a theme is never
 * worth breaking the app over: anything unreadable falls back to `system`.
 */
export function readStoredPreference(): ThemePreference {
  try {
    const stored: unknown = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isThemePreference(stored) ? stored : 'system';
  } catch {
    return 'system';
  }
}

/** Persists the preference. A storage failure leaves the theme applied for this tab and is not fatal. */
export function storePreference(preference: ThemePreference): void {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, preference);
  } catch {
    // Ignored on purpose: see readStoredPreference.
  }
}

/**
 * The OS preference query, or null where `matchMedia` does not exist — jsdom under Vitest, and older
 * browsers. Absent means "no opinion", which resolves to the light theme.
 */
function systemQuery(): MediaQueryList | null {
  return typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-color-scheme: dark)')
    : null;
}

export function prefersDark(): boolean {
  return systemQuery()?.matches ?? false;
}

export function resolveTheme(preference: ThemePreference): ResolvedTheme {
  if (preference === 'system') {
    return prefersDark() ? 'dark' : 'light';
  }

  return preference;
}

/**
 * Stamps the resolved theme on `<html>`, which is the only selector the stylesheet reads.
 *
 * Always an explicit `light` or `dark`, never the raw preference: that is what lets `globals.css` define
 * the dark palette once instead of duplicating it under a `prefers-color-scheme` media query.
 */
export function applyTheme(theme: ResolvedTheme): void {
  document.documentElement.dataset.theme = theme;
}

/**
 * Calls back when the OS preference changes, so a session left on `system` follows the machine going
 * dark at sunset without a reload. Returns the matching unsubscribe.
 */
export function watchSystemTheme(onChange: (theme: ResolvedTheme) => void): () => void {
  const query = systemQuery();

  if (query === null) {
    return (): void => {
      // Nothing subscribed, nothing to release.
    };
  }

  const handleChange = (event: MediaQueryListEvent): void => {
    onChange(event.matches ? 'dark' : 'light');
  };

  query.addEventListener('change', handleChange);

  return (): void => {
    query.removeEventListener('change', handleChange);
  };
}
